/**
 * Checklist Tab (Shared between Admin and Process Owner)
 * 
 * Manages checklist questions and responses
 * Features:
 * - Admin: Add/Edit/Delete questions, view PO responses (read-only)
 * - PO: Answer questions, save responses (required before certification)
 * 
 * Usage:
 * - Admin Tab 4: Configure questions for selected PO, view responses
 * - PO Tab 3: Respond to questions from Admin
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Button, Input, message, Dropdown, Table } from 'antd';
import type { MenuProps, TableColumnsType } from 'antd';
import ConfirmModal from '../../../../../components/common/ConfirmModal';
import { EditOutlined, DeleteOutlined, SearchOutlined, MoreOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import ExportPanel from '../../../../../components/common/ExportPanel';
import ChevronDownIcon from '../../../../../assets/icons/ic-chevron-down.svg?react';
import ArrowUpIcon from '../../../../../assets/icons/ic-arrow-up.svg?react';
import WarningIcon from '../../../../../assets/icons/ic-warning.svg';
import AddItemButton from '../../../../../components/common/AddItemButton';
import ChecklistItemDrawer, { type ChecklistItemFormData } from './ChecklistItemDrawer';
import ChecklistResponseDrawer, { type ChecklistResponseFormData } from './ChecklistResponseDrawer';
import { RootState, AppDispatch } from '../../../../../store';
import {
  addDraftQuestion,
  insertDraftQuestion,
  updateDraftQuestion,
  deleteDraftQuestion,
  commitDraftChecklist,
  updateChecklistResponse,
  setChecklistSaved,
} from '../../../../../store/slices/certificationSlice';
import {
  submitChecklist,
  deleteChecklistItem,
  type Quarter,
  type ProcessOwner,
  type ChecklistItem,
  type SignificantChangeStatus,
} from '../../../../../services/certificationService';

// ========================================
// Props Interface
// ========================================

interface ChecklistTabProps {
  selectedQuarter: Quarter;
  onQuarterChange: (quarter: Quarter) => void;
  // Admin-specific props
  isAdmin: boolean;
  selectedProcessOwner?: ProcessOwner | null;
  processOwners?: ProcessOwner[];
  onProcessOwnerChange?: (ownerId: string) => void;
  // Loading state from parent
  isLoading?: boolean;
  // Unsaved changes modal - for tab change from parent (CertificationPage)
  pendingTabChange?: string | null;
  onConfirmTabChange?: () => void;
  onCancelTabChange?: () => void;
  // Export trigger from parent
  triggerExport?: boolean;
  onExportClose?: () => void;
}

// ========================================
// Styled Components
// ========================================

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const HeaderControls = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const TopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 24px;
`;

const LeftControls = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

// Quarter Chip (dropdown trigger)
const QuarterChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  height: 38px;
  background: #F0FDF4;
  color: #166534;
  border-radius: 18px;
  font-family: Inter;
  font-weight: 500;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.005em;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    opacity: 0.8;
  }
`;

const QuarterChevronIcon = styled.div`
  display: flex;
  align-items: center;
  color: #166534;
  
  svg {
    width: 14px;
    height: 14px;
  }
  
  svg path {
    stroke: #166534;
  }
`;

const QuarterDropdownMenu = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.1);
  padding: 8px;
  min-width: 120px;
`;

const QuarterDropdownItem = styled.div<{ $selected?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  background: ${props => props.$selected ? '#F0FDF4' : 'white'};
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  color: ${props => props.$selected ? '#166534' : '#1F2937'};

  &:hover {
    background: ${props => props.$selected ? '#F0FDF4' : '#F3F4F6'};
  }
`;

// Process Owner Chip (from RiskAssessmentDetailPage style)
const ProcessOwnerChip = styled.div<{ $clickable?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  height: 38px;
  background: #EFF6FF;
  color: #1E40AF;
  border-radius: 18px;
  font-family: Inter;
  font-weight: 500;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.005em;
  cursor: ${({ $clickable }) => $clickable ? 'pointer' : 'default'};
  transition: all 0.2s;
  
  ${({ $clickable }) => $clickable && `
    &:hover {
      opacity: 0.8;
    }
  `}
`;

const ChevronIcon = styled.div`
  display: flex;
  align-items: center;
  color: #1E40AF;
  
  svg {
    width: 14px;
    height: 14px;
  }
  
  svg path {
    stroke: #1E40AF;
  }
`;

// Dropdown styles
const DropdownMenu = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.1);
  padding: 8px;
  width: 280px;
  max-height: 320px;
  overflow-y: auto;
`;

const SearchInput = styled(Input)`
  margin-bottom: 8px;
  border-radius: 6px;
  height: 38px;
  
  .ant-input {
    font-family: Inter;
    font-size: 14px;
  }
`;

const DropdownItem = styled.div<{ $selected?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  background: ${props => props.$selected ? '#EFF6FF' : 'white'};

  &:hover {
    background: ${props => props.$selected ? '#EFF6FF' : '#F3F4F6'};
  }
`;

const DropdownItemAvatar = styled.div`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #EFF6FF;
  border-radius: 50%;
  font-family: Inter;
  font-weight: 600;
  font-size: 12px;
  color: #1E40AF;
  flex-shrink: 0;
`;

const DropdownItemText = styled.span<{ $selected?: boolean }>`
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  color: ${props => props.$selected ? '#2563EB' : '#1F2937'};
  flex: 1;
`;

const TableContainer = styled.div`
  background: #E0E7FF;
  border-radius: 6px;
  border: 1px solid #E0E7FF;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  padding: 2px;

  .ant-table {
    border-radius: 4px;
    overflow: hidden;
  }

  .ant-table-thead > tr > th {
    background: #F8FAFC;
    font-family: Inter;
    font-weight: 500;
    font-size: 12px;
    color: #6B7280;
    padding: 12px 16px;
    border-bottom: 1px solid #E5E7EB;
    border-right: 1px solid #E5E7EB;
    
    &:last-child {
      border-right: none;
    }
    
    &::before {
      display: none !important;
    }
  }

  .ant-table-tbody > tr > td {
    font-family: Inter;
    font-size: 14px;
    color: #1F2937;
    padding: 12px 16px;
    border-bottom: 1px solid #E5E7EB;
    border-right: 1px solid #E5E7EB;
    vertical-align: middle;
    
    &:last-child {
      border-right: none;
    }
  }

  .ant-table-tbody > tr:last-child > td {
    border-bottom: none;
  }

  .ant-table-tbody > tr:hover > td {
    background: #F9FAFB;
  }
`;

const QuestionText = styled.div`
  line-height: 1.6;
`;

const SignificantChangeBadge = styled.span<{ $status: SignificantChangeStatus | null }>`
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 16px;
  font-family: Inter;
  font-weight: 500;
  font-size: 12px;
  
  ${({ $status }) => {
    // Only Change Identified and No Changes have distinct styles
    // Everything else (na, null, empty) uses N/A style
    switch ($status) {
      case 'changeIdentified':
        return `
          background: #FEE2E2;
          color: #DC2626;
        `;
      case 'noChanges':
        return `
          background: #DBEAFE;
          color: #2563EB;
        `;
      default:
        // N/A style for 'na', null, or any other value
        return `
          background: #F3F4F6;
          color: #6B7280;
        `;
    }
  }}
`;

const ResponseText = styled.span`
  max-width: 300px;
  color: #6B7280;
  display: block;
`;

const ActionButton = styled.button`
  background: #F8FAFC;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  padding: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #6B7280;
  transition: all 0.3s;

  &:hover {
    color: #1F2937;
    background: #EFF6FF;
  }

  .anticon {
    font-size: 20px;
  }
`;

const MenuItemContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    flex-shrink: 0;
  }
`;

const MenuItemText = styled.span`
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.005em;
  color: #1F2937;
`;

const MenuItemTextDanger = styled(MenuItemText)`
  color: #DC2626;
`;

const FooterActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background: white;
  border-radius: 0 0 6px 6px;
  border-top: 1px solid #E5E7EB;
`;

const FooterLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const FooterRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const StyledFilledButton = styled(Button)`
  height: 40px;
  padding: 0 24px;
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  background: #2563EB;
  border-color: #2563EB;
  color: #FFFFFF;
  
  &:hover:not(:disabled) {
    background: #1D4ED8 !important;
    border-color: #1D4ED8 !important;
    color: #FFFFFF !important;
  }
  
  &:disabled {
    background: #9CA3AF !important;
    border-color: #9CA3AF !important;
  }
`;

const SavedIndicator = styled.span`
  font-family: Inter;
  font-size: 13px;
  color: #059669;
`;

const EmptyStateWrapper = styled.div`
  text-align: center;
  padding: 60px 20px;
  background: white;
  border-radius: 6px;
`;

const EmptyStateText = styled.div`
  color: #1F2937;
  margin-bottom: 16px;
  font-family: Inter;
  font-size: 15px;
  font-weight: 500;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 16px;
  min-height: 400px;

  .spinner {
    width: 48px;
    height: 48px;
    border: 4px solid #E5E7EB;
    border-top-color: #2563EB;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

// ========================================
// Component
// ========================================

const ChecklistTab: React.FC<ChecklistTabProps> = ({
  selectedQuarter,
  onQuarterChange,
  isAdmin,
  selectedProcessOwner,
  processOwners = [],
  onProcessOwnerChange,
  isLoading: parentIsLoading = false,
  // Props for tab change from parent
  pendingTabChange,
  onConfirmTabChange,
  onCancelTabChange,
  // Export trigger from parent
  triggerExport,
  onExportClose,
}) => {
  const dispatch = useDispatch<AppDispatch>();

  const { selectedReport } = useSelector((state: RootState) => state.annualReport);
  const {
    quarterData,
    draftChecklistItems,
    hasUnsavedChanges,
    isChecklistSaved,
  } = useSelector((state: RootState) => state.certification);

  const { isExternalUser } = useSelector((state: RootState) => state.auth);

  const [saving, setSaving] = useState(false);
  const [ownerSearchTerm, setOwnerSearchTerm] = useState('');
  const [ownerDropdownOpen, setOwnerDropdownOpen] = useState(false);
  const [quarterDropdownOpen, setQuarterDropdownOpen] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  
  // Admin Drawer states (for adding/editing questions)
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<'add' | 'edit'>('add');
  const [editingItem, setEditingItem] = useState<ChecklistItem | null>(null);
  const [insertAtIndex, setInsertAtIndex] = useState<number | null>(null);
  
  // PO Response Drawer state
  const [responseDrawerOpen, setResponseDrawerOpen] = useState(false);
  const [respondingItem, setRespondingItem] = useState<ChecklistItem | null>(null);
  
  // Unsaved changes modal state (for quarter/process owner/tab changes)
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [pendingChange, setPendingChange] = useState<{
    type: 'quarter' | 'processOwner' | 'tab';
    value: Quarter | string;
  } | null>(null);

  // Export panel state
  const [isExportPanelOpen, setIsExportPanelOpen] = useState(false);

  // Determine which process owner to use - always from selectedProcessOwner (Redux)
  // API returns process owner list (1 item for PO role, multiple for Admin)
  const effectiveProcessOwnerId = selectedProcessOwner?.id;

  // Get certificationQuarterId from quarterData (fetched by parent CertificationPage)
  const certificationQuarterId = quarterData?.certificationQuarterId;

  // Warn on browser close/refresh with unsaved changes (Admin only)
  useEffect(() => {
    if (!isAdmin) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges, isAdmin]);

  // Watch for pending tab change from parent (CertificationPage)
  useEffect(() => {
    if (pendingTabChange && hasUnsavedChanges) {
      setPendingChange({ type: 'tab', value: pendingTabChange });
      setShowUnsavedModal(true);
    }
  }, [pendingTabChange, hasUnsavedChanges]);

  // Watch for triggerExport from parent
  useEffect(() => {
    if (triggerExport) {
      setIsExportPanelOpen(true);
    }
  }, [triggerExport]);

  // Handler for quarter change with unsaved changes check
  const handleQuarterChangeWithCheck = useCallback((quarter: Quarter) => {
    if (hasUnsavedChanges) {
      setPendingChange({ type: 'quarter', value: quarter });
      setShowUnsavedModal(true);
    } else {
      onQuarterChange(quarter);
    }
    setQuarterDropdownOpen(false);
  }, [hasUnsavedChanges, onQuarterChange]);

  // Handler for process owner change with unsaved changes check
  const handleProcessOwnerChangeWithCheck = useCallback((ownerId: string) => {
    if (hasUnsavedChanges) {
      setPendingChange({ type: 'processOwner', value: ownerId });
      setShowUnsavedModal(true);
    } else {
      onProcessOwnerChange?.(ownerId);
    }
    setOwnerSearchTerm('');
    setOwnerDropdownOpen(false);
  }, [hasUnsavedChanges, onProcessOwnerChange]);

  // Handle confirm - proceed with pending change (discard unsaved changes)
  const handleConfirmUnsavedChange = useCallback(() => {
    if (pendingChange) {
      if (pendingChange.type === 'quarter') {
        onQuarterChange(pendingChange.value as Quarter);
      } else if (pendingChange.type === 'processOwner') {
        onProcessOwnerChange?.(pendingChange.value as string);
      } else if (pendingChange.type === 'tab') {
        // Tab change - call parent callback
        onConfirmTabChange?.();
      }
    }
    setShowUnsavedModal(false);
    setPendingChange(null);
  }, [pendingChange, onQuarterChange, onProcessOwnerChange, onConfirmTabChange]);

  // Handle cancel - stay on current selection
  const handleCancelUnsavedChange = useCallback(() => {
    // If this was a tab change, notify parent
    if (pendingChange?.type === 'tab') {
      onCancelTabChange?.();
    }
    setShowUnsavedModal(false);
    setPendingChange(null);
  }, [pendingChange, onCancelTabChange]);

  // Open drawer for adding new question
  const openAddDrawer = useCallback((atIndex?: number) => {
    setDrawerMode('add');
    setEditingItem(null);
    setInsertAtIndex(atIndex ?? null);
    setDrawerOpen(true);
  }, []);

  // Open drawer for editing existing question
  const openEditDrawer = useCallback((item: ChecklistItem) => {
    setDrawerMode('edit');
    setEditingItem(item);
    setInsertAtIndex(null);
    setDrawerOpen(true);
  }, []);

  // Handle add question (Admin only)
  const handleAddQuestion = useCallback((item: ChecklistItem) => {
    const index = draftChecklistItems.findIndex(i => i.checklistLineItemId === item.checklistLineItemId);
    openAddDrawer(index);
  }, [draftChecklistItems, openAddDrawer]);

  // Handle add question below (Admin only)
  // const handleAddQuestionBelow = useCallback((item: ChecklistItem) => {
  //   const index = draftChecklistItems.findIndex(i => i.checklistLineItemId === item.checklistLineItemId);
  //   openAddDrawer(index + 1);
  // }, [draftChecklistItems, openAddDrawer]);

  // Handle drawer submit (add or edit)
  const handleDrawerSubmit = useCallback((formData: ChecklistItemFormData) => {
    if (drawerMode === 'add') {
      if (insertAtIndex !== null) {
        // Insert at specific index
        dispatch(insertDraftQuestion({ question: formData.question, atIndex: insertAtIndex }));
      } else {
        // Append to end
        dispatch(addDraftQuestion(formData.question));
      }
      message.success('Question added (draft)');
    } else if (drawerMode === 'edit' && editingItem) {
      dispatch(updateDraftQuestion({
        id: editingItem.checklistLineItemId,
        question: formData.question,
      }));
      message.success('Question updated (draft)');
    }
    
    setDrawerOpen(false);
    setEditingItem(null);
    setInsertAtIndex(null);
  }, [drawerMode, insertAtIndex, editingItem, dispatch]);

  // Handle delete question (Admin only)
  // Mark item as deleted locally - actual API delete is deferred to handleSubmit
  // This allows user to undo by refreshing page before submitting
  const handleDeleteQuestion = useCallback((id: string) => {
    dispatch(deleteDraftQuestion(id));
    message.success('Question marked for deletion. Submit to confirm.');
  }, [dispatch]);

  // Handle PO response drawer open
  const handleOpenResponseDrawer = useCallback((item: ChecklistItem) => {
    setRespondingItem(item);
    setResponseDrawerOpen(true);
  }, []);

  // Handle PO response drawer submit
  const handleResponseDrawerSubmit = useCallback((data: ChecklistResponseFormData) => {
    dispatch(updateChecklistResponse({
      id: data.checklistLineItemId,
      significantChange: data.significantChange ?? undefined,
      response: data.response,
    }));
    setResponseDrawerOpen(false);
    setRespondingItem(null);
    message.success('Response updated');
  }, [dispatch]);

  // Export handler
  const handleCloseExportPanel = useCallback(() => {
    setIsExportPanelOpen(false);
    onExportClose?.();
  }, [onExportClose]);

  // Handle submit/save
  const handleSubmit = useCallback(async () => {
    if (!selectedReport || !effectiveProcessOwnerId || !certificationQuarterId) {
      message.error('Missing required data. Please try again.');
      return;
    }

    setSaving(true);
    try {
      // Step 1: Delete items that are marked for deletion (existing items only)
      // Draft items (new, not yet submitted) are simply removed from local state
      const itemsToDelete = draftChecklistItems.filter(item => 
        item.isDeleted && 
        !item.isNew && 
        item.checklistLineItemId && 
        !item.checklistLineItemId.startsWith('draft-')
      );

      if (itemsToDelete.length > 0) {
        // console.log('📋 ChecklistTab: Deleting items...', { deleteCount: itemsToDelete.length });
        
        // Delete items in parallel
        await Promise.all(
          itemsToDelete.map(item => deleteChecklistItem(item.checklistLineItemId))
        );
        
        // console.log('✅ ChecklistTab: Items deleted successfully');
      }

      // Step 2: Submit remaining items (non-deleted)
      // Admin: Send question and certificationQuarterId
      //   - For existing items: also include checklistLineItemId AND existing response/significantChange
      //   - For new items: only question and certificationQuarterId
      // PO: Send all fields including checklistKeyChange and response
      const items = draftChecklistItems
        .filter(item => !item.isDeleted)
        .map(item => {
          if (isAdmin) {
            // Check if this is an existing item (has real ID, not draft ID)
            const isExistingItem = !item.isNew && item.checklistLineItemId && !item.checklistLineItemId.startsWith('draft-');
            
            if (isExistingItem) {
              // Existing item: include checklistLineItemId and preserve existing response/significantChange
              return {
                checklistLineItemId: item.checklistLineItemId,
                question: item.question,
                checklistKeyChange: item.significantChange || undefined,
                response: item.response || undefined,
                certificationQuarterId: certificationQuarterId,
              };
            } else {
              // New item: only question and certificationQuarterId
              return {
                question: item.question,
                certificationQuarterId: certificationQuarterId,
              };
            }
          } else {
            // PO sends all fields
            return {
              checklistLineItemId: item.isNew ? undefined : item.checklistLineItemId,
              question: item.question,
              checklistKeyChange: item.significantChange || 'noChanges',
              response: item.response,
              certificationQuarterId: certificationQuarterId,
            };
          }
        });

      // console.log('📋 ChecklistTab: Submitting checklist...', { itemCount: items.length, certificationQuarterId, isAdmin });

      await submitChecklist({ items });
      dispatch(commitDraftChecklist());

      if (!isAdmin) {
        dispatch(setChecklistSaved(true));
      }

      message.success(isAdmin ? 'Checklist published successfully' : 'Checklist saved successfully');
    } catch (error) {
      console.error('Error submitting checklist:', error);
      message.error(isAdmin ? 'Failed to publish checklist' : 'Failed to save checklist');
    } finally {
      setSaving(false);
    }
  }, [selectedReport, effectiveProcessOwnerId, certificationQuarterId, draftChecklistItems, dispatch, isAdmin]);

  // Get status label - only show Change Identified / No Changes / N/A
  // Any other value (null, empty, etc.) maps to N/A
  const getStatusLabel = (status: SignificantChangeStatus | null) => {
    switch (status) {
      case 'changeIdentified':
        return 'Change Identified';
      case 'noChanges':
        return 'No Changes';
      default:
        return 'N/A';
    }
  };

  // Filter out deleted items for display
  const visibleItems = draftChecklistItems.filter(item => !item.isDeleted);
  
  // Check if there are items pending deletion (for empty state with submit button)
  const hasPendingDeletions = draftChecklistItems.some(item => item.isDeleted);

  // Prepare export data (must be after visibleItems declaration)
  const exportData = useMemo(() => {
    return visibleItems.map((item, index) => ({
      index: index + 1,
      question: item.question,
      significantChange: getStatusLabel(item.significantChange),
      response: item.response || '-',
    }));
  }, [visibleItems]);

  // Filter process owners for search
  const filteredOwners = ownerSearchTerm
    ? processOwners.filter(po =>
      po.processOwnerName.toLowerCase().includes(ownerSearchTerm.toLowerCase())
    )
    : processOwners;

  // Get owner initials for avatar
  const getOwnerInitials = (name: string) => {
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Get display name for process owner
  // Get display name - always from selectedProcessOwner
  const getProcessOwnerDisplayName = () => {
    return selectedProcessOwner?.processOwnerName || '';
  };

  // Process Owner dropdown content
  const ownerDropdownContent = (
    <DropdownMenu onClick={(e) => e.stopPropagation()}>
      <SearchInput
        prefix={<SearchOutlined />}
        placeholder="Search process owner..."
        value={ownerSearchTerm}
        onChange={(e) => setOwnerSearchTerm(e.target.value)}
        autoFocus
      />
      {filteredOwners.map(po => {
        const isSelected = selectedProcessOwner?.id === po.id;
        return (
          <DropdownItem
            key={po.id}
            $selected={isSelected}
            onClick={() => handleProcessOwnerChangeWithCheck(po.id)}
          >
            <DropdownItemAvatar>
              {getOwnerInitials(po.processOwnerName)}
            </DropdownItemAvatar>
            <DropdownItemText $selected={isSelected}>
              {po.processOwnerName}
            </DropdownItemText>
          </DropdownItem>
        );
      })}
      {filteredOwners.length === 0 && (
        <div style={{ padding: '16px', textAlign: 'center', color: '#9CA3AF', fontSize: 14 }}>
          No process owners found
        </div>
      )}
    </DropdownMenu>
  );

  // Get menu items for question actions (Admin only)
  const getQuestionMenuItems = (item: ChecklistItem): MenuProps['items'] => [
     {
      key: 'add-above',
      label: (
        <MenuItemContent>
          <ArrowUpIcon />
          <MenuItemText>Add Question</MenuItemText>
        </MenuItemContent>
      ),
      onClick: () => handleAddQuestion(item),
    },
    // {
    //   key: 'add-below',
    //   label: (
    //     <MenuItemContent>
    //       <ArrowDownIcon />
    //       <MenuItemText>Add row below</MenuItemText>
    //     </MenuItemContent>
    //   ),
    //   onClick: () => handleAddQuestionBelow(item),
    // },
    {
      key: 'edit',
      label: (
        <MenuItemContent>
          <EditOutlined />
          <MenuItemText>Edit</MenuItemText>
        </MenuItemContent>
      ),
      onClick: () => openEditDrawer(item),
    },
    {
      key: 'delete',
      label: (
        <MenuItemContent>
          <DeleteOutlined style={{ color: '#DC2626' }} />
          <MenuItemTextDanger>Delete</MenuItemTextDanger>
        </MenuItemContent>
      ),
      onClick: () => setDeleteConfirmId(item.checklistLineItemId),
    },
  ];

  // Table columns definition
  const columns: TableColumnsType<ChecklistItem> = useMemo(() => [
    {
      title: '#',
      key: 'index',
      width: 60,
      align: 'center' as const,
      render: (_: unknown, __: ChecklistItem, index: number) => index + 1,
    },
    {
      title: 'Change Indicator Question',
      dataIndex: 'question',
      key: 'question',
      render: (text: string) => <QuestionText>{text}</QuestionText>,
    },
    {
      title: 'Significant Change',
      dataIndex: 'significantChange',
      key: 'significantChange',
      width: 150,
      align: 'center' as const,
      render: (status: SignificantChangeStatus | null) => (
        <SignificantChangeBadge $status={status}>
          {getStatusLabel(status)}
        </SignificantChangeBadge>
      ),
    },
    {
      title: 'Response',
      dataIndex: 'response',
      key: 'response',
      width: 300,
      render: (text: string) => <ResponseText>{text || '-'}</ResponseText>,
    },
    {
      title: 'Action',
      key: 'action',
      width: 80,
      align: 'center' as const,
      render: (_: unknown, record: ChecklistItem) => (
        isAdmin ? (
          <Dropdown
            menu={{ items: getQuestionMenuItems(record) }}
            trigger={['click']}
            placement="bottomRight"
          >
            <ActionButton onClick={(e) => e.stopPropagation()}>
              <MoreOutlined />
            </ActionButton>
          </Dropdown>
        ) : (
          <ActionButton onClick={() => handleOpenResponseDrawer(record)}>
            <EditOutlined />
          </ActionButton>
        )
      ),
    },
  ], [isAdmin, handleOpenResponseDrawer]);

  // Show loading state when parent is loading
  if (parentIsLoading) {
    return (
      <LoadingContainer>
        <div className="spinner" />
      </LoadingContainer>
    );
  }
  
  // Render header controls (used in both empty state and normal state)
  const renderHeaderControls = () => (
    <HeaderControls>
      <TopRow>
        <LeftControls>
          {/* Quarter Dropdown */}
          <Dropdown
            dropdownRender={() => (
              <QuarterDropdownMenu>
                {([1, 2, 3, 4] as Quarter[]).map(q => (
                  <QuarterDropdownItem
                    key={q}
                    $selected={selectedQuarter === q}
                    onClick={() => handleQuarterChangeWithCheck(q)}
                  >
                    Q{q}
                  </QuarterDropdownItem>
                ))}
              </QuarterDropdownMenu>
            )}
            trigger={['click']}
            placement="bottomLeft"
            open={quarterDropdownOpen}
            onOpenChange={setQuarterDropdownOpen}
          >
            <QuarterChip>
              Q{selectedQuarter}
              <QuarterChevronIcon>
                <ChevronDownIcon />
              </QuarterChevronIcon>
            </QuarterChip>
          </Dropdown>
        </LeftControls>

        {/* Process Owner Chip - Admin with dropdown, PO read-only */}
        {isAdmin && selectedProcessOwner && onProcessOwnerChange ? (
          <Dropdown
            dropdownRender={() => ownerDropdownContent}
            trigger={['click']}
            placement="bottomRight"
            open={ownerDropdownOpen}
            onOpenChange={setOwnerDropdownOpen}
          >
            <ProcessOwnerChip $clickable>
              Process Owner: {getProcessOwnerDisplayName()}
              <ChevronIcon>
                <ChevronDownIcon />
              </ChevronIcon>
            </ProcessOwnerChip>
          </Dropdown>
        ) : (
          <ProcessOwnerChip>
            Process Owner: {getProcessOwnerDisplayName()}
          </ProcessOwnerChip>
        )}
      </TopRow>
    </HeaderControls>
  );
  
  // Show empty state when quarterData is null (API returned no data)
  // This typically means the Process Owner hasn't been assigned to any processes yet
  // Still show header controls so user can change process owner or quarter
  if (!quarterData) {
    return (
      <Container>
        {renderHeaderControls()}
        <EmptyStateWrapper>
          <EmptyStateText>
            {isAdmin 
              ? "This process owner hasn't been assigned to any processes yet. You must assign the process owner to a process before you can create change indicator questions."
              : "You haven't been assigned to any processes yet. Please contact your administrator."
            }
          </EmptyStateText>
        </EmptyStateWrapper>
      </Container>
    );
  }

  return (
    <Container>
      {/* Header Controls */}
      <HeaderControls>
        <TopRow>
          <LeftControls>
            {/* Quarter Dropdown */}
            <Dropdown
              dropdownRender={() => (
                <QuarterDropdownMenu>
                  {([1, 2, 3, 4] as Quarter[]).map(q => (
                    <QuarterDropdownItem
                      key={q}
                      $selected={selectedQuarter === q}
                      onClick={() => handleQuarterChangeWithCheck(q)}
                    >
                      Q{q}
                    </QuarterDropdownItem>
                  ))}
                </QuarterDropdownMenu>
              )}
              trigger={['click']}
              placement="bottomLeft"
              open={quarterDropdownOpen}
              onOpenChange={setQuarterDropdownOpen}
            >
              <QuarterChip>
                Q{selectedQuarter}
                <QuarterChevronIcon>
                  <ChevronDownIcon />
                </QuarterChevronIcon>
              </QuarterChip>
            </Dropdown>
          </LeftControls>

          {/* Process Owner Chip - Admin with dropdown, PO read-only */}
          {isAdmin && selectedProcessOwner && onProcessOwnerChange ? (
            <Dropdown
              dropdownRender={() => ownerDropdownContent}
              trigger={['click']}
              placement="bottomRight"
              open={ownerDropdownOpen}
              onOpenChange={setOwnerDropdownOpen}
            >
              <ProcessOwnerChip $clickable>
                Process Owner: {getProcessOwnerDisplayName()}
                <ChevronIcon>
                  <ChevronDownIcon />
                </ChevronIcon>
              </ProcessOwnerChip>
            </Dropdown>
          ) : (
            <ProcessOwnerChip>
              Process Owner: {getProcessOwnerDisplayName()}
            </ProcessOwnerChip>
          )}
        </TopRow>
      </HeaderControls>

      {/* Table */}
      {visibleItems.length === 0 ? (
        <EmptyStateWrapper>
          {hasPendingDeletions ? (
            // Show pending deletions message with submit button (Admin only can delete)
            <>
              <EmptyStateText>
                All questions have been marked for deletion.
              </EmptyStateText>
              <span style={{ color: '#DC2626', fontSize: 13, marginBottom: 16, display: 'block' }}>
                You have unsaved changes
              </span>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
                <AddItemButton
                  text="Add Question"
                  onClick={() => openAddDrawer()}
                />
                <StyledFilledButton
                  type="primary"
                  loading={saving}
                  onClick={handleSubmit}
                >
                  Save Checklist
                </StyledFilledButton>
              </div>
            </>
          ) : (
            // Normal empty state - no questions defined
            <>
              <EmptyStateText>
                {isAdmin
                  ? "No checklist questions have been defined for this process owner."
                  : "No checklist questions have been configured for this quarter."
                }
              </EmptyStateText>
              {isAdmin && (
                <AddItemButton
                  text="Add Question"
                  onClick={() => openAddDrawer()}
                />
              )}
            </>
          )}
        </EmptyStateWrapper>
      ) : (
        <TableContainer>
          <Table
            dataSource={visibleItems}
            columns={!isExternalUser ? columns : columns.filter(col => col.title != 'Action')}
            rowKey="checklistLineItemId"
            pagination={false}
            size="middle"
          />

          <FooterActions>
            <FooterLeft>
              {!isAdmin && isChecklistSaved && !hasUnsavedChanges && (
                <SavedIndicator>✓ Checklist saved</SavedIndicator>
              )}
              {hasUnsavedChanges && (
                <span style={{ color: '#DC2626', fontSize: 13 }}>
                  You have unsaved changes
                </span>
              )}
            </FooterLeft>
            <FooterRight>
              <StyledFilledButton
                type="primary"
                loading={saving}
                disabled={isAdmin && !hasUnsavedChanges}
                onClick={handleSubmit}
              >
                Save Checklist
              </StyledFilledButton>
            </FooterRight>
          </FooterActions>
        </TableContainer>
      )}

      {/* Checklist Item Drawer (Admin only) */}
      {isAdmin && (
        <ChecklistItemDrawer
          open={drawerOpen}
          mode={drawerMode}
          initialValues={editingItem}
          insertAtIndex={insertAtIndex}
          onClose={() => {
            setDrawerOpen(false);
            setEditingItem(null);
            setInsertAtIndex(null);
          }}
          onSubmit={handleDrawerSubmit}
        />
      )}

      {/* Delete Confirmation Modal (Admin only) */}
      {isAdmin && (
        <ConfirmModal
          open={!!deleteConfirmId}
          onClose={() => setDeleteConfirmId(null)}
          onConfirm={() => {
            if (deleteConfirmId) {
              handleDeleteQuestion(deleteConfirmId);
              setDeleteConfirmId(null);
            }
          }}
          title="Delete Question"
          description="Are you sure you want to delete this question? This will also remove any response provided by the process owner."
          confirmText="Delete"
          cancelText="Cancel"
          danger
        />
      )}

      {/* Response Drawer (PO only) */}
      {!isAdmin && (
        <ChecklistResponseDrawer
          open={responseDrawerOpen}
          item={respondingItem}
          onClose={() => {
            setResponseDrawerOpen(false);
            setRespondingItem(null);
          }}
          onSubmit={handleResponseDrawerSubmit}
        />
      )}

      {/* Unsaved Changes Modal (for quarter/process owner/tab changes) */}
      <ConfirmModal
        open={showUnsavedModal}
        onClose={handleCancelUnsavedChange}
        onConfirm={handleConfirmUnsavedChange}
        icon={WarningIcon}
        title="Unsaved Changes"
        description={
          pendingChange?.type === 'tab'
            ? "You have unsaved changes. Are you sure you want to leave this page? Your changes will be lost."
            : "You have unsaved changes. Are you sure you want to continue? Your changes will be lost."
        }
        confirmText={pendingChange?.type === 'tab' ? 'Leave' : 'Continue'}
        cancelText="Stay"
        danger
      />

      {/* Export Panel */}
      <ExportPanel
        open={isExportPanelOpen}
        onClose={handleCloseExportPanel}
        data={exportData}
        columns={[
          { key: 'index', title: '#' },
          { key: 'question', title: 'Change Indicator Question' },
          { key: 'significantChange', title: 'Significant Change' },
          { key: 'response', title: 'Response' },
        ]}
        fileName={`Checklist_Q${selectedQuarter}_${selectedProcessOwner?.processOwnerName?.replace(/\s+/g, '_') || 'Report'}`}
        title={`Checklist Q${selectedQuarter} - ${selectedProcessOwner?.processOwnerName || ''}`}
      />

    </Container>
  );
};

export default ChecklistTab;

